// Runtime image-format negotiation for the planet textures.
//
// scripts/encode-textures.mjs writes a WebP re-encode alongside every
// JPEG source that survives an SSIM check against that source, and
// records which ones made the cut in src/data/textureFormats.js. The
// re-encodes are visually lossless and roughly a third of the bytes.
// That matters because the eager texture set is the whole payload
// standing between a first visit and a usable viewer, and a large share
// of this site's traffic is mobile on constrained connections.
//
// Support is probed by actually decoding a 1x1 lossy WebP rather than
// asking canvas.toDataURL('image/webp'). Safari could decode WebP for
// several releases before it could encode it, so the encode test would
// send those browsers back to the JPEGs for no reason.

import { WEBP_TEXTURES } from './data/textureFormats.js';
import { MOBILE_TEXTURES } from './data/mobileTextures.js';
import { RUNNING_ON_MOBILE } from './mobiledevice.js';

const PROBE = 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEADMDOJaQAA3AA/uuuAAA=';

let supportsWebp = false;
let probePromise = null;

export function webpSupported() {
  return supportsWebp;
}

// Resolves once the probe settles. Safe to call repeatedly; the probe
// only ever runs once. Never rejects — a failed probe means JPEG, which
// is the status quo.
export function detectWebpSupport() {
  if (probePromise) return probePromise;
  probePromise = new Promise((resolve) => {
    if (typeof Image === 'undefined') {
      resolve(false);
      return;
    }
    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      supportsWebp = ok;
      resolve(ok);
    };
    const img = new Image();
    img.onload = () => finish(img.width === 1 && img.height === 1);
    img.onerror = () => finish(false);
    // A decode that never settles must not hold up the loading screen.
    setTimeout(() => finish(false), 3000);
    img.src = PROBE;
  });
  return probePromise;
}

// '/textures/mars.jpg' -> '/textures/mars.webp', preserving any
// cache-busting query string. Anything without an accepted WebP sibling
// is returned untouched, so a rejected or missing encode can never 404.
export function resolveTextureUrl(url) {
  if (!supportsWebp || typeof url !== 'string') return url;
  // Staged replacement maps (?tex=next) have no WebP twins.
  if (url.includes('/textures/next/')) return url;
  const match = url.match(/^([^?#]*\/)([^/?#]+)\.jpg(\?[^#]*)?$/i);
  if (!match) return url;
  const [, dir, base, query = ''] = match;
  if (!WEBP_TEXTURES.has(base)) return url;
  return `${dir}${base}.webp${query}`;
}

// Rewrites a { key: url } map in place. Used on the shared textureUrls
// map so every consumer (loadTextureByName, findLoadedTextureByUrl, the
// moon retry path) sees one consistent set of URLs.
export function applyTextureFormat(map) {
  if (!supportsWebp || !map) return map;
  for (const key of Object.keys(map)) {
    map[key] = resolveTextureUrl(map[key]);
  }
  return map;
}

// On a phone, '/textures/earth.jpg' -> '/textures/mobile/earth.jpg', the
// 2048 copy scripts/build-mobile-textures.mjs writes (query kept), so the
// phone decodes only the pixels it keeps.
// Anything without a copy, and every URL off a phone, is returned untouched.
export function mobileTextureUrl(url, mobile = RUNNING_ON_MOBILE) {
  if (!mobile || typeof url !== 'string') return url;
  const match = url.match(/^\/textures\/([^/?#]+)(\?[^#]*)?$/);
  if (!match || !MOBILE_TEXTURES.has(match[1])) return url;
  return `/textures/mobile/${match[1]}${match[2] || ''}`;
}

// The { key: url } map in place, as applyTextureFormat (run that first: the
// copies keep their source's format).
export function applyMobileTextures(map, mobile = RUNNING_ON_MOBILE) {
  if (!mobile || !map) return map;
  for (const key of Object.keys(map)) map[key] = mobileTextureUrl(map[key], mobile);
  return map;
}
